import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Type,
  Hash,
  Calendar,
  CheckSquare,
  Circle,
  ChevronDown,
  Image,
  PenTool,
  MapPin,
  AlignLeft,
  Trash2,
  GripVertical,
  Wand2,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { FormField } from "@/data/system-forms";

interface FormBuilderProps {
  onSave: (formData: any) => void;
  initialData?: any;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio', label: 'Radio', icon: Circle },
  { value: 'select', label: 'Dropdown', icon: ChevronDown },
  { value: 'photo', label: 'Photo Upload', icon: Image },
  { value: 'signature', label: 'Signature', icon: PenTool },
  { value: 'location', label: 'Location', icon: MapPin },
  { value: 'textarea', label: 'Text Area', icon: AlignLeft },
];

function SortableField({
  field,
  onUpdate,
  onDelete,
}: {
  field: FormField;
  onUpdate: (field: FormField) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const FieldIcon = FIELD_TYPES.find((t) => t.value === field.type)?.icon || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-card border border-border rounded-lg p-4 space-y-3"
    >
      <div className="flex items-start gap-3">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1">
          <GripVertical className="w-5 h-5 text-muted-foreground" />
        </div>
        
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2">
            <FieldIcon className="w-4 h-4 text-primary" />
            <Badge variant="secondary" className="text-xs">
              {field.type}
            </Badge>
            {field.required && (
              <Badge variant="destructive" className="text-xs">
                Required
              </Badge>
            )}
          </div>

          <div className="grid gap-2">
            <Input
              placeholder="Field Label"
              value={field.label}
              onChange={(e) => onUpdate({ ...field, label: e.target.value })}
            />
            
            {(field.type === 'text' || field.type === 'textarea') && (
              <Input
                placeholder="Placeholder text"
                value={field.placeholder || ''}
                onChange={(e) => onUpdate({ ...field, placeholder: e.target.value })}
              />
            )}

            {(field.type === 'checkbox' || field.type === 'radio' || field.type === 'select') && (
              <Textarea
                placeholder="Options (one per line)"
                value={field.options?.join('\n') || ''}
                onChange={(e) =>
                  onUpdate({ ...field, options: e.target.value.split('\n').filter((o) => o.trim()) })
                }
                rows={3}
              />
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                checked={field.required}
                onCheckedChange={(checked) => onUpdate({ ...field, required: !!checked })}
              />
              <Label className="text-sm">Required field</Label>
            </div>
          </div>
        </div>

        <Button variant="ghost" size="icon" onClick={onDelete} className="text-destructive">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function FormBuilder({ onSave, initialData }: FormBuilderProps) {
  const { orgId } = useAuth();
  const [formName, setFormName] = useState(initialData?.name || '');
  const [formDescription, setFormDescription] = useState(initialData?.description || '');
  const [formCategory, setFormCategory] = useState<string>(initialData?.category || 'inspection');
  const [fields, setFields] = useState<FormField[]>(initialData?.fields || []);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addField = (type: string) => {
    const newField: FormField = {
      id: `field-${Date.now()}`,
      type: type as any,
      label: `New ${type} field`,
      required: false,
      ...(type === 'checkbox' || type === 'radio' || type === 'select' ? { options: ['Option 1', 'Option 2'] } : {}),
    };
    setFields([...fields, newField]);
  };

  const updateField = (index: number, updatedField: FormField) => {
    const newFields = [...fields];
    newFields[index] = updatedField;
    setFields(newFields);
  };

  const deleteField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!formName.trim()) {
      toast.error("Please enter a form name");
      return;
    }
    if (fields.length === 0) {
      toast.error("Please add at least one field");
      return;
    }

    onSave({
      name: formName,
      description: formDescription,
      category: formCategory,
      fields,
    });
  };

  const handleAIGenerate = async () => {
    const prompt = aiPrompt.trim() || formDescription.trim() || formName.trim();
    if (!prompt) {
      toast.error("Describe what you want (in AI prompt, description, or name)");
      return;
    }
    if (!orgId) { toast.error("No organization"); return; }
    setAiGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-form-with-ai', {
        body: { prompt, orgId, category: formCategory },
      });
      if (error) throw error;
      const form = data?.form;
      if (!form) throw new Error('No form returned');
      setFormName(form.name || formName);
      setFormDescription(form.description || formDescription);
      setFormCategory(form.category || formCategory);
      setFields((form.form_fields || []) as FormField[]);
      setAiPrompt('');
      toast.success("Form generated — review and save");
    } catch (e: any) {
      toast.error(e?.message || "AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Form Details</CardTitle>
          <CardDescription>Basic information about your form</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="form-name">Form Name *</Label>
            <Input
              id="form-name"
              placeholder="e.g., Pre-Work Inspection"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="form-description">Description</Label>
            <Textarea
              id="form-description"
              placeholder="What is this form used for?"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="form-category">Category</Label>
            <Select value={formCategory} onValueChange={setFormCategory}>
              <SelectTrigger id="form-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-prompt">AI Prompt (optional)</Label>
            <Textarea
              id="ai-prompt"
              placeholder="e.g. Roof inspection form with pitch, deck condition, damage photos, and homeowner signature"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={2}
            />
          </div>
          <Button onClick={handleAIGenerate} variant="outline" className="w-full gap-2" disabled={aiGenerating}>
            <Wand2 className="w-4 h-4" />
            {aiGenerating ? 'Generating…' : 'Generate with AI'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Form Fields</CardTitle>
          <CardDescription>Add and arrange fields for your form</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {FIELD_TYPES.map((fieldType) => {
              const Icon = fieldType.icon;
              return (
                <Button
                  key={fieldType.value}
                  variant="outline"
                  size="sm"
                  onClick={() => addField(fieldType.value)}
                  className="gap-2"
                >
                  <Icon className="w-4 h-4" />
                  {fieldType.label}
                </Button>
              );
            })}
          </div>

          {fields.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Plus className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Click a button above to add your first field</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <SortableField
                      key={field.id}
                      field={field}
                      onUpdate={(updatedField) => updateField(index, updatedField)}
                      onDelete={() => deleteField(index)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3 justify-end">
        <Button variant="outline">Cancel</Button>
        <Button onClick={handleSave}>Save Form Template</Button>
      </div>
    </div>
  );
}
